import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { ProgressBar } from "@/components/ProgressBar";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Assessment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/nurse/modules/${id}/assessment`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e)); navigate(`/app/modules/${id}`); });
  }, [id]);

  if (!data) return <Loader />;
  const q = data.questions[idx];
  const total = data.questions.length;
  const setAns = (val) => setAnswers({ ...answers, [q.id]: val });
  const current = answers[q.id];

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: data.questions.map((qq) => {
          const a = answers[qq.id];
          return qq.type === "mcq"
            ? { question_id: qq.id, selected_index: a ?? null }
            : { question_id: qq.id, text: a || "" };
        }),
      };
      const r = await api.post(`/nurse/modules/${id}/assessment/submit`, payload);
      navigate(`/app/results/${r.data.result_id}`, { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#fcf9f8]">
      <div className="max-w-md mx-auto px-4 py-6 pb-28" data-testid="assessment-page">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-[#434654]" data-testid="question-counter">Question {idx + 1} of {total}</span>
          <span className="text-xs text-[#434654]">{data.pass_threshold}% to pass</span>
        </div>
        <ProgressBar value={((idx + 1) / total) * 100} testId="assessment-progress" />

        <h2 className="text-lg font-semibold text-[#1c1b1b] mt-6 mb-4" data-testid="question-text">{q.text}</h2>

        {q.type === "mcq" ? (
          <div className="space-y-3">
            {q.options.map((opt, i) => (
              <button
                key={i}
                data-testid={`answer-option-${i}`}
                onClick={() => setAns(i)}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  current === i ? "border-[#003d9b] bg-[#003d9b]/5 ring-1 ring-[#003d9b]" : "border-[#E5E7EB] bg-white hover:border-[#0052cc]"
                }`}
              >
                <span className="text-[15px] text-[#1c1b1b]">{opt}</span>
              </button>
            ))}
          </div>
        ) : (
          <textarea
            data-testid="short-answer-input"
            value={current || ""}
            onChange={(e) => setAns(e.target.value)}
            placeholder="Type your answer…"
            className="min-h-[140px] w-full rounded-lg border border-[#E5E7EB] bg-white p-4 text-[16px] focus:outline-none focus:ring-2 focus:ring-[#003d9b]"
          />
        )}

        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-[#E5E7EB] p-3 flex gap-3">
          <button
            data-testid="prev-question-btn"
            disabled={idx === 0}
            onClick={() => setIdx(idx - 1)}
            className="flex-1 h-12 rounded-lg border border-[#E5E7EB] font-semibold text-[#1c1b1b] disabled:opacity-40 flex items-center justify-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          {idx < total - 1 ? (
            <button
              data-testid="next-question-btn"
              onClick={() => setIdx(idx + 1)}
              className="flex-1 h-12 rounded-lg bg-[#003d9b] text-white font-semibold flex items-center justify-center gap-1 hover:bg-[#0052cc] transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="submit-assessment-btn"
              onClick={submit}
              disabled={submitting}
              className="flex-1 h-12 rounded-lg bg-[#004e33] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
